import { commonUtils } from '@sunrise/shared'
import {
    AmsRequestKeys,
    AmsSearchRequest,
    DeliveryOption,
    Filter,
    FilterKeys,
    MakeAndModelName,
} from '../../services/amsSearch/amsSearch.interface'

interface FeatureFilter {
    filterKey: FilterKeys
    featureKey: string
}

interface Tag {
    value: any
    label?: string
    key?: AmsRequestKeys
    featureFilter?: FeatureFilter
}

export const createTags = (
    managedData: AmsSearchRequest,
    managedFilters: Filter[],
    makeAndModelName?: MakeAndModelName,
): Tag[] => {
    const vehicleFeatureTags = createVehicleFeatureFilterTags(managedFilters)

    const tags = createFilterTags(managedData, makeAndModelName, vehicleFeatureTags)

    return tags.filter((tag) => tag.value && tag.label !== 'Alle Angebote')
}

const createVehicleFeatureFilterTags = (filters: Filter[]): Tag[] => {
    return filters.flatMap((filter) => {
        return filter.values.map((filterValue) => {
            return {
                value: filterValue.selected,
                label: filterValue.label,
                featureFilter: {
                    filterKey: filter.key,
                    featureKey: filterValue.key,
                },
            }
        })
    })
}

const createFilterTags = (
    managedData: AmsSearchRequest,
    makeAndModelName?: MakeAndModelName,
    additionalTags?: Tag[],
): Tag[] => {
    return [
        {
            value: makeAndModelName?.makeName,
            label: makeAndModelName?.makeName,
            key: AmsRequestKeys.MakeId,
        },
        {
            value: makeAndModelName?.modelName,
            label: makeAndModelName?.modelName,
            key: AmsRequestKeys.ModelId,
        },
        {
            value: managedData[AmsRequestKeys.PriceMin],
            label: `Ab ${commonUtils.thousandSeparator(managedData[AmsRequestKeys.PriceMin] ?? 0)} €`,
            key: AmsRequestKeys.PriceMin,
        },
        {
            value: managedData[AmsRequestKeys.PriceMax],
            label: `Bis ${commonUtils.thousandSeparator(managedData[AmsRequestKeys.PriceMax] ?? 0)} €`,
            key: AmsRequestKeys.PriceMax,
        },
        {
            value: managedData[AmsRequestKeys.FirstRegistrationDateMin],
            label: `Von ${managedData[AmsRequestKeys.FirstRegistrationDateMin]}`,
            key: AmsRequestKeys.FirstRegistrationDateMin,
        },
        {
            value: managedData[AmsRequestKeys.FirstRegistrationDateMax],
            label: `Bis ${managedData[AmsRequestKeys.FirstRegistrationDateMax]}`,
            key: AmsRequestKeys.FirstRegistrationDateMax,
        },
        {
            value: managedData[AmsRequestKeys.MileageMin],
            label: `Ab ${commonUtils.thousandSeparator(managedData[AmsRequestKeys.MileageMin] ?? 0)} km`,
            key: AmsRequestKeys.MileageMin,
        },
        {
            value: managedData[AmsRequestKeys.MileageMax],
            label: `Bis ${commonUtils.thousandSeparator(managedData[AmsRequestKeys.MileageMax] ?? 0)} km`,
            key: AmsRequestKeys.MileageMax,
        },
        {
            value: managedData[AmsRequestKeys.DeliveryOption],
            label: createDeliveryOptionLabel(managedData[AmsRequestKeys.DeliveryOption]),
            key: AmsRequestKeys.DeliveryOption,
        },
        {
            value: managedData[AmsRequestKeys.LocationName],
            label: createLocationLabel(managedData[AmsRequestKeys.LocationName], managedData[AmsRequestKeys.Radius]),
            key: AmsRequestKeys.LocationName,
        },
        ...(additionalTags ?? []),
        {
            value: managedData[AmsRequestKeys.HpMin],
            label: `Von ${managedData[AmsRequestKeys.HpMin]} PS`,
            key: AmsRequestKeys.HpMin,
        },
        {
            value: managedData[AmsRequestKeys.HpMax],
            label: `Bis ${managedData[AmsRequestKeys.HpMax]} PS`,
            key: AmsRequestKeys.HpMax,
        },
        {
            value: managedData[AmsRequestKeys.KwMin],
            label: `Von ${managedData[AmsRequestKeys.KwMin]} kW`,
            key: AmsRequestKeys.KwMin,
        },
        {
            value: managedData[AmsRequestKeys.KwMax],
            label: `Bis ${managedData[AmsRequestKeys.KwMax]} kW`,
            key: AmsRequestKeys.KwMax,
        },
        {
            value: managedData[AmsRequestKeys.SeatsMin],
            label: `${managedData[AmsRequestKeys.SeatsMin]} Sitze`,
            key: AmsRequestKeys.SeatsMin,
        },
        {
            value: managedData[AmsRequestKeys.DoorsMin],
            label: `${managedData[AmsRequestKeys.DoorsMin]}/${managedData[AmsRequestKeys.DoorsMax]} Türen`,
            key: AmsRequestKeys.DoorsMin,
        },
    ]
}

const createDeliveryOptionLabel = (deliveryOption: DeliveryOption) => {
    switch (deliveryOption) {
        case DeliveryOption.All:
            return 'Alle Angebote'
        case DeliveryOption.OnSite:
            return 'Nur vor Ort kaufen'
        case DeliveryOption.OnLine:
            return 'Online-Kauf'
    }
}

const createLocationLabel = (locationName: string, radius: number) => {
    if (locationName === 'Deutschland') {
        return locationName
    } else {
        return `${locationName}, Umkreis ${radius} km`
    }
}
